<script setup>
import { computed, ref, useId } from "vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import InputError from "@/Components/InputError.vue";
import "/node_modules/flag-icons/css/flag-icons.min.css";

/**
 * Country chips + search combobox. Used by EventTrackAlertModal and (with :chips="false", as a plain
 * "add a country" search) by EventTrackAlertManager.
 */
const selected = defineModel({ type: Array, default: () => [] }); // ISO2, e.g. ["LV", "EE"]
const props = defineProps({
    countries: { type: Array, required: true }, // [{ id, name }]
    error: { type: String, default: null },
    chips: { type: Boolean, default: true }, // false: no label, no chips, Backspace removes nothing
    label: { type: String, default: "Countries" },
});

const query = ref("");
const activeOption = ref(0);
const searchInput = ref(null);
const uid = useId(); // unique per instance: the page and the EventTrackAlertModal can both show a picker

const names = computed(() => Object.fromEntries(props.countries.map((c) => [c.id, c.name])));
const matches = computed(() => {
    const q = query.value.trim().toLowerCase();
    if (!q) {
        return [];
    }
    return props.countries.filter((c) => !selected.value.includes(c.id) && c.name.toLowerCase().includes(q)).slice(0, 8);
});

function add(id) {
    if (id && !selected.value.includes(id)) {
        selected.value = [...selected.value, id];
    }
    query.value = "";
    activeOption.value = 0;
}

function remove(id) {
    selected.value = selected.value.filter((c) => c !== id);
}

function onKeydown(event) {
    if (event.key === "Backspace" && props.chips && query.value === "" && selected.value.length) {
        remove(selected.value.at(-1));
    } else if (event.key === "ArrowDown" && matches.value.length) {
        event.preventDefault();
        activeOption.value = (activeOption.value + 1) % matches.value.length;
    } else if (event.key === "ArrowUp" && matches.value.length) {
        event.preventDefault();
        activeOption.value = (activeOption.value - 1 + matches.value.length) % matches.value.length;
    } else if (event.key === "Enter" && query.value !== "") {
        // An empty search lets Enter submit the form.
        event.preventDefault();
        if (matches.value.length) {
            add(matches.value[activeOption.value]?.id);
        }
    } else if (event.key === "Escape" && query.value !== "") {
        // Close only the list, not the modal around it.
        event.stopPropagation();
        query.value = "";
    }
}

defineExpose({
    focus: () => searchInput.value?.focus(),
    clear: () => (query.value = ""),
});
</script>

<template>
    <div>
        <div
            class="mb-1 flex items-baseline justify-between"
            :class="{ 'sr-only': !chips }"
        >
            <label
                :for="`${uid}-search`"
                class="text-sm font-medium text-gray-700"
                >{{ label }}</label
            >
            <span
                v-if="chips"
                class="text-xs text-gray-500"
                >Add as many as you like</span
            >
        </div>
        <div class="relative">
            <div
                class="flex flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-blue-300 focus-within:ring focus-within:ring-blue-200 focus-within:ring-opacity-50"
                @click="searchInput?.focus()"
            >
                <span
                    v-for="id in chips ? selected : []"
                    :key="id"
                    class="inline-flex items-center gap-1 rounded-full bg-blue-100 py-0.5 pl-2.5 pr-1 text-sm text-blue-800"
                >
                    <span
                        :class="`fi fi-${id.toLowerCase()} border`"
                        aria-hidden="true"
                    ></span>
                    {{ names[id] ?? id }}
                    <button
                        type="button"
                        class="rounded-full p-0.5 hover:bg-blue-200"
                        :aria-label="`Remove ${names[id] ?? id}`"
                        @click.stop="remove(id)"
                    >
                        <XMarkIcon class="size-3.5" />
                    </button>
                </span>
                <input
                    :id="`${uid}-search`"
                    ref="searchInput"
                    v-model="query"
                    type="text"
                    role="combobox"
                    aria-autocomplete="list"
                    :aria-controls="`${uid}-options`"
                    :aria-expanded="matches.length > 0"
                    :aria-activedescendant="matches.length ? `${uid}-option-${activeOption}` : null"
                    autocomplete="off"
                    placeholder="Search countries…"
                    class="min-w-[8rem] flex-1 border-0 p-1 text-sm text-gray-900 focus:ring-0"
                    @input="activeOption = 0"
                    @keydown="onKeydown"
                    @blur="query = ''"
                />
            </div>
            <ul
                v-if="matches.length"
                :id="`${uid}-options`"
                role="listbox"
                @mousedown.prevent
                class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
            >
                <li
                    v-for="(country, index) in matches"
                    :id="`${uid}-option-${index}`"
                    :key="country.id"
                    role="option"
                    :aria-selected="index === activeOption"
                    class="flex cursor-pointer items-center gap-2 px-3 py-1.5"
                    :class="index === activeOption ? 'bg-blue-50 text-blue-800' : 'text-gray-800'"
                    @mousedown.prevent="add(country.id)"
                    @mouseenter="activeOption = index"
                >
                    <span
                        :class="`fi fi-${country.id.toLowerCase()} border`"
                        aria-hidden="true"
                    ></span>
                    {{ country.name }}
                </li>
            </ul>
        </div>
        <InputError
            :message="error"
            class="mt-1"
        />
    </div>
</template>
